import { PlaidConnectionErrors } from "@common/constants";
import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";
import { orgReconnectStrategy } from "./orgReconnectStrategy";

/**
 * Who connected the integration, from the row's point of view.
 *
 * `client` is an approximation: it means "somebody other than me", so a
 * teammate's connection also lands here. The row carries no client-vs-firm-member
 * role, only `connectedBy.userId`, and that is the only trustworthy comparison —
 * emails are nullable on both sides. `unknown` covers a connection whose creator
 * cannot be resolved, which has nobody to email and nothing to reconnect.
 */
export type IntegrationRowAudience = "accountant" | "client" | "unknown";

export interface IntegrationRowAction {
    isVisible: boolean;
    isEnabled: boolean;
    disabledReason?: string;
}

export interface IntegrationRowActions {
    audience: IntegrationRowAudience;
    reconnect: IntegrationRowAction;
    remind: IntegrationRowAction;
    remove: IntegrationRowAction;
}

interface IntegrationRowActionsParams {
    item: OrganizationIntegrationAccountItem;
    /** `null` while the session is still loading — nothing is actionable yet. */
    currentUserId: number | null;
    /**
     * Connections already emailed in this session. The 24h cooldown lives only
     * in Redis and is on no schema, so this is the only pre-emption available.
     */
    remindedConnectionIds: ReadonlySet<string>;
}

const HEALTHY_REASON =
    "This connection is working, so there's nothing to reconnect.";

const hidden: IntegrationRowAction = { isVisible: false, isEnabled: false };

const blocked = (disabledReason: string): IntegrationRowAction => ({
    isVisible: true,
    isEnabled: false,
    disabledReason,
});

const allowed: IntegrationRowAction = { isVisible: true, isEnabled: true };

const audienceOf = ({
    item,
    currentUserId,
}: Pick<
    IntegrationRowActionsParams,
    "item" | "currentUserId"
>): IntegrationRowAudience => {
    const connectedByUserId = item.connection.connectedBy?.userId;
    if (
        currentUserId === null ||
        connectedByUserId === undefined ||
        connectedByUserId === null
    ) {
        return "unknown";
    }
    return connectedByUserId === currentUserId ? "accountant" : "client";
};

/**
 * A row is one account, and `item.connection` deliberately omits its sibling
 * accounts — so this is the only workspace id reachable from a row. There is no
 * fallback: an unassigned account genuinely has no client to scope the call to.
 */
const workspaceIdOf = (item: OrganizationIntegrationAccountItem) =>
    item.account.entity?.workspaceId ?? null;

/**
 * Derived from `orgReconnectStrategy` rather than from provider lists of its own.
 * That registry is a total `Record` over the provider enum, so a provider added
 * to the schema is a compile error there instead of silently falling through to
 * `allowed` here and skipping the guards below.
 */
const reconnectAction = (
    item: OrganizationIntegrationAccountItem,
): IntegrationRowAction => {
    const { connection } = item;
    const strategy = orgReconnectStrategy(connection.provider);

    if (strategy.kind === "unsupported") {
        return blocked(
            "Reconnect this integration from the client's workspace.",
        );
    }
    if (connection.status !== "disconnected") {
        return blocked(HEALTHY_REASON);
    }
    if (connection.error === PlaidConnectionErrors.ITEM_NOT_SUPPORTED) {
        return blocked(
            "The institution doesn't support this connection, so reconnecting won't fix it. Contact Kick support if you need further assistance.",
        );
    }
    if (connection.error === PlaidConnectionErrors.ITEM_REMOVED) {
        return blocked(
            "Plaid removed this connection, so reconnecting won't fix it. Connect the bank again to resume syncing.",
        );
    }

    const isNumericIdEndpoint =
        strategy.kind === "plaid" ||
        (strategy.kind === "oauthPopup" && strategy.target !== "native");
    if (
        isNumericIdEndpoint &&
        !Number.isInteger(Number(connection.providerConnectionId))
    ) {
        return blocked("This connection can't be reconnected from here.");
    }

    // The native fix endpoint sits behind WorkspaceAccessGuard.
    const isWorkspaceScoped =
        strategy.kind === "paypal" ||
        (strategy.kind === "oauthPopup" && strategy.target === "native");
    if (isWorkspaceScoped && workspaceIdOf(item) === null) {
        return blocked("Assign this account to a client before reconnecting.");
    }

    return allowed;
};

const remindAction = ({
    item,
    audience,
    remindedConnectionIds,
}: {
    item: OrganizationIntegrationAccountItem;
    audience: IntegrationRowAudience;
    remindedConnectionIds: ReadonlySet<string>;
}): IntegrationRowAction => {
    if (audience === "unknown") {
        return blocked("No one is on record as having connected this account.");
    }
    if (item.connection.status !== "disconnected") {
        return blocked(HEALTHY_REASON);
    }
    if (remindedConnectionIds.has(item.connection.id)) {
        return blocked("A reminder was already sent for this connection.");
    }
    return allowed;
};

/**
 * Which row-actions menu items to show and whether each is actionable.
 *
 * Two menus, chosen by audience: the person who connected an integration is the
 * one who can reconnect it, so a row someone else connected offers to email them
 * instead. Removal is deliberately UI-gated to that same person even though the
 * server would allow a firm admin to remove any account.
 */
export const integrationRowActions = ({
    item,
    currentUserId,
    remindedConnectionIds,
}: IntegrationRowActionsParams): IntegrationRowActions => {
    const audience = audienceOf({ item, currentUserId });

    if (audience === "accountant") {
        return {
            audience,
            reconnect: reconnectAction(item),
            remind: hidden,
            remove: allowed,
        };
    }

    return {
        audience,
        reconnect: hidden,
        remind: remindAction({ item, audience, remindedConnectionIds }),
        remove: blocked(
            "Only the person who connected this account can remove it.",
        ),
    };
};
